import { supabase } from '../supabaseClient'
import { filasSync, type EstadoGuardado } from './sync-estados'
import { cuentaParaGT11, estadoChecklist, type FirmaResumen, type ResultadoPregunta } from './permisos-trabajo'

const TODOS_LOS_ITEMS_PERMISOS = ['GT-09', 'GT-10', 'GT-11']
// Lote D (M49): si la empresa ya tiene procedimientos de trabajos especiales, GT 37 (GT-09) lo calcula
// informes-puesto/ip-checklist.ts (procedimiento aprobado y socializado por tipo usado) y aquí no se escribe.
export const sinGT09 = (items: string[], usaProcedimientos: boolean) => (usaProcedimientos ? items.filter(i => i !== 'GT-09') : items)

const TODOS_LOS_TIPOS_PERMISO = [
  'altura', 'caliente', 'confinado', 'electrico', 'quimico',
  'loto', 'excavacion', 'maquinaria', 'vias',
]

interface WorkPermitForCompliance {
  id: string
  permit_type: string
  status: string
}

export async function syncWorkPermitCompliance(companyId: string): Promise<void> {
  if (!companyId) return

  const { data: permits } = await supabase
    .from('work_permits')
    .select('id, permit_type, status')
    .eq('company_id', companyId)

  const permitList = (permits ?? []) as WorkPermitForCompliance[]

  // GT-09: procedimientos documentados y ejecutados en los 9 tipos de trabajo especial
  const tiposConPermiso = new Set(permitList.map(p => p.permit_type))
  const gt09Cumple = TODOS_LOS_TIPOS_PERMISO.every(tipo => tiposConPermiso.has(tipo))

  // GT-10: al menos un permiso emitido formalmente (activo o cerrado; los
  // pendientes de firmas no cuentan)
  const gt10Cumple = permitList.some(p => p.status === 'activo' || p.status === 'cerrado')

  // GT-11: al menos un permiso cerrado con firma de cierre y checklist completo
  // sin "No cumple" (catálogo y preguntas propias; decisión de JC 19/10/2026)
  const permitsCerrados = permitList.filter(p => p.status === 'cerrado')
  let gt11Cumple = false

  if (permitsCerrados.length > 0) {
    const tiposCerrados = Array.from(new Set(permitsCerrados.map(p => p.permit_type)))
    const permitIds = permitsCerrados.map(p => p.id)

    const [{ data: checklistItems }, { data: responses }, { data: custom }, { data: firmas }] = await Promise.all([
      supabase.from('work_permit_checklist_items').select('id, permit_type').in('permit_type', tiposCerrados),
      supabase.from('work_permit_responses').select('permit_id, checklist_item_id, resultado').in('permit_id', permitIds),
      supabase.from('work_permit_custom_items').select('permit_id, resultado').in('permit_id', permitIds),
      supabase.from('work_permit_signatures').select('permit_id, etapa, rol').in('permit_id', permitIds),
    ])

    gt11Cumple = permitsCerrados.some(p => {
      const respuestas = new Map(
        ((responses ?? []) as { permit_id: string; checklist_item_id: string; resultado: ResultadoPregunta | null }[])
          .filter(r => r.permit_id === p.id)
          .map(r => [r.checklist_item_id, r.resultado]),
      )
      const catalogo = ((checklistItems ?? []) as { id: string; permit_type: string }[])
        .filter(i => i.permit_type === p.permit_type)
        .map(i => ({ resultado: respuestas.get(i.id) ?? null }))
      const propias = ((custom ?? []) as { permit_id: string; resultado: ResultadoPregunta | null }[])
        .filter(c => c.permit_id === p.id)
      const firmasPermiso = ((firmas ?? []) as (FirmaResumen & { permit_id: string })[])
        .filter(f => f.permit_id === p.id)
      return cuentaParaGT11(p.status, estadoChecklist(catalogo, propias), firmasPermiso)
    })
  }

  const cumpleActual: string[] = []
  if (gt09Cumple) cumpleActual.push('GT-09')
  if (gt10Cumple) cumpleActual.push('GT-10')
  if (gt11Cumple) cumpleActual.push('GT-11')

  const { count: procedimientos, error: errProc } = await supabase
    .from('special_work_procedures').select('id', { count: 'exact', head: true }).eq('company_id', companyId)
  const items = sinGT09(TODOS_LOS_ITEMS_PERMISOS, !errProc && (procedimientos ?? 0) > 0)

  const { data: existing } = await supabase
    .from('checklist_item_estados')
    .select('item_id, estado, auto_source')
    .eq('company_id', companyId)
    .in('item_id', items)

  // Bloque 6, D: manda el módulo (sync-estados.ts): Cumple, En proceso si hay datos o Pendiente; nunca pisa un "No aplica".
  const rows = filasSync(companyId, items, cumpleActual, permitList.length > 0 ? items : [], (existing ?? []) as EstadoGuardado[], 'workpermits')

  if (rows.length === 0) return

  await supabase.from('checklist_item_estados').upsert(rows, { onConflict: 'company_id,item_id' })
}
